import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  SearchFilterBar,
  type FilterSettings,
} from "@/components/ui/search-filter-bar";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { PageHeading } from "@/components/ui/page-heading";
import {
  DEFAULT_DEPARTMENTS,
  Employee,
  UserRole,
  formatCurrency,
} from "@employee-management/common";
import { AllReportsTable } from "./components";
import {
  apiService,
  appSettingsService,
  reportsService,
} from "@/lib/api-service";
import type {
  ReportFilters,
  ReportData,
  AllReportData,
  AggregatedReportData,
} from "@/types/reports.types";
import { AppSettings } from "@/types/app-settings.types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Activity, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SalesMember } from "@/types/project.types";

export const ReportsView = () => {
  const [filters, setFilters] = useState<ReportFilters>({
    page: 1,
    limit: 10,
    search: "",
    sortBy: "totalHours",
    sortOrder: "desc",
    dateFrom: "",
    dateTo: "",
    department: "",
    employee: "",
    supervisor: "",
    client: "",
    project: "",
  });

  // Fetch report data
  const {
    data: reportResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["reports", "all", JSON.stringify(filters)],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters.page) params.append("page", filters.page.toString());
      if (filters.limit) params.append("limit", filters.limit.toString());
      if (filters.search) params.append("search", filters.search);
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);
      if (filters.department) params.append("department", filters.department);
      if (filters.employee) params.append("employee", filters.employee);
      if (filters.supervisor) params.append("supervisor", filters.supervisor);
      if (filters.client) params.append("client", filters.client);
      if (filters.project) params.append("project", filters.project);

      const res = await apiService.getPaginated<ReportData | AllReportData>(
        `/reports/all?${params.toString()}`
      );
      return res;
    },
    enabled: true, // Always enabled for real-time data
  });

  // Fetch aggregated report data
  const { data: aggregatedResponse } = useQuery({
    queryKey: ["reports", "all-aggregated", JSON.stringify(filters)],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters.search) params.append("search", filters.search);
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);
      if (filters.department) params.append("department", filters.department);
      if (filters.employee) params.append("employee", filters.employee);
      if (filters.supervisor) params.append("supervisor", filters.supervisor);
      if (filters.client) params.append("client", filters.client);
      if (filters.project) params.append("project", filters.project);

      const res = await apiService.get<AggregatedReportData>(
        `/reports/all-aggregated?${params.toString()}`
      );
      return res;
    },
    enabled: true,
  });

  // Fetch departments from app settings
  const { data: appSettings } = useQuery<AppSettings>({
    queryKey: ["app-settings"],
    queryFn: () => appSettingsService.getAll(),
  });

  // Fetch all employees
  const { data: employeesList = [] } = useQuery<Employee[]>({
    queryKey: ["employees"],
    queryFn: () => apiService.get<Employee[]>("/employees"),
  });

  // Fetch client names
  const { data: clientNames = [] } = useQuery<string[]>({
    queryKey: ["client-list"],
    queryFn: () => reportsService.getClientList(),
  });

  // Fetch internal members (employees from default departments)
  const { data: internalMembers = [] } = useQuery({
    queryKey: ["sales-members"],
    queryFn: async () =>
      apiService.get<SalesMember[]>("/employees/sales-members"),
  });

  const handleFilterChange = (filterSettings: FilterSettings) => {
    setFilters((prev) => ({
      ...prev,
      search: filterSettings.searchTerm,
      dateFrom: (filterSettings.filters.dateRange as any)?.min || "",
      dateTo: (filterSettings.filters.dateRange as any)?.max || "",
      department: (filterSettings.filters.department as string) || "",
      employee: (filterSettings.filters.employee as string) || "",
      supervisor: (filterSettings.filters.supervisor as string) || "",
      client: (filterSettings.filters.client as string) || "",
      project: (filterSettings.filters.project as string) || "",
      page: 1, // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  };

  const handleSortChange = (sortBy: string, sortOrder: "asc" | "desc") => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder,
    }));
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      // Add all current filters to the export
      if (filters.search) params.append("search", filters.search);
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);
      if (filters.department) params.append("department", filters.department);
      if (filters.employee) params.append("employee", filters.employee);
      if (filters.supervisor) params.append("supervisor", filters.supervisor);
      if (filters.client) params.append("client", filters.client);
      if (filters.project) params.append("project", filters.project);
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

      // Add export parameter
      params.append("export", "true");

      const response = await apiService.getBlob(
        `/reports/all?${params.toString()}`
      );

      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reports_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("Export completed successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error instanceof Error ? error.message : "Export failed");
    }
  };

  const employeeOptions = useMemo(
    () =>
      employeesList
        ?.filter(
          (employee) =>
            !DEFAULT_DEPARTMENTS.includes(employee.department as any)
        )
        .filter(
          (employee) =>
            filters.department === "all" ||
            !Boolean(filters.department) ||
            employee.department === filters.department
        )
        .map((employee) => ({
          value: employee.id || "",
          label: employee.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || [],
    [employeesList, filters.department]
  );

  const internalEmployeeOptions = useMemo(
    () =>
      internalMembers
        .map((employee) => ({
          value: employee.id || "",
          label: employee.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || [],
    [employeesList, filters.department]
  );

  // Filter options
  const filterOptions = useMemo(() => {
    return [
      {
        key: "dateRange",
        label: "Date Range",
        type: "range" as const,
        rangeType: "date" as const,
      },
      {
        key: "department",
        label: "Department",
        type: "select" as const,
        options:
          appSettings?.department_list
            ?.sort((a, b) => a.localeCompare(b))
            .map((dept) => ({
              value: dept,
              label: dept,
            })) || [],
      },
      {
        key: "employee",
        label: "Employee",
        type: "select" as const,
        options: employeeOptions,
      },
      {
        key: "supervisor",
        label: "Supervisor/Manager",
        type: "select" as const,
        options: internalEmployeeOptions,
      },
      {
        key: "client",
        label: "Client",
        type: "select" as const,
        options: clientNames.map((clientName) => ({
          value: clientName,
          label: clientName,
        })),
      },
    ];
  }, [
    appSettings?.department_list,
    employeeOptions,
    internalEmployeeOptions,
    clientNames,
  ]);

  return (
    <ProtectedRoute requiredRole={UserRole.SUPERADMIN}>
      <div className="space-y-6">
        <PageHeading
          title="Reports"
          subtitle="View overtime reports and analytics across different dimensions."
          actions={
            <Button
              variant="outline"
              onClick={handleExport}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          }
        />

        {/* Reports Content */}
        <div className="space-y-6">
          <div>
            {/* Filter Bar */}
            <SearchFilterBar
              filterOptions={filterOptions}
              placeholder="Search by employee, project, client, or supervisor..."
              className="mb-6"
              onFilterChange={handleFilterChange}
            />

            {/* Aggregated Summary */}
            {aggregatedResponse && (
              <div className="my-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Approved Status */}
                  <Card className="bg-background">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Activity className="h-4 w-4 text-green-600" />
                        Approved Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Hours:
                        </span>
                        <span className="font-medium">
                          {aggregatedResponse.totalApprovedStatusHours.toFixed(
                            1
                          )}
                          h
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Amount:
                        </span>
                        <span className="font-medium">
                          {formatCurrency(
                            aggregatedResponse.totalApprovedStatusAmount
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Entries:
                        </span>
                        <Badge variant="secondary">
                          {aggregatedResponse.totalApprovedStatusEntriesCount}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Payment Approved */}
                  <Card className="bg-background">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-green-600" />
                        Payment Approved
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Amount:
                        </span>
                        <span className="font-medium">
                          {formatCurrency(
                            aggregatedResponse.paymentApprovedAmount
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Count:
                        </span>
                        <Badge variant="secondary">
                          {aggregatedResponse.paymentApprovedCount}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Payment Pending */}
                  <Card className="bg-background">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-yellow-600" />
                        Payment Pending
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Amount:
                        </span>
                        <span className="font-medium">
                          {formatCurrency(
                            aggregatedResponse.paymentPendingAmount
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Count:
                        </span>
                        <Badge variant="secondary">
                          {aggregatedResponse.paymentPendingCount}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="text-muted-foreground">Loading reports...</div>
              </div>
            )}

            {/* Error State */}
            {isError && (
              <div className="flex items-center justify-center py-8">
                <div className="text-red-500">Failed to load reports.</div>
              </div>
            )}

            {/* Table */}
            {!isLoading && !isError && reportResponse && (
              <AllReportsTable
                data={(reportResponse.data as AllReportData[]) || []}
                pagination={reportResponse.pagination}
                onPageChange={handlePageChange}
                onSortChange={handleSortChange}
                currentSort={{
                  sortBy: filters.sortBy,
                  sortOrder: filters.sortOrder,
                }}
              />
            )}

            {/* Empty State */}
            {!isLoading &&
              !isError &&
              reportResponse &&
              reportResponse.data &&
              reportResponse.data.length === 0 && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">No data to show</div>
                </div>
              )}
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};
